import React, {useEffect, useRef, useState} from "react";
import {AmusnetJackpotWidgetConfiguration} from "../../services/configurations/config-data-sections/amusnet-jackpot-widget.configuration"
import {JackpotWidgetAspectRatioBox} from "../jackpot-widget/boxes/aspect-ratio/jackpot-widget-aspect-ratio.box";
import {JACKPOT_WIDGET_LAYOUT} from "../jackpot-widget/jackpot-widget.component";
import {useServices} from "../../react-hooks/use-services.hook";
import {JackpotWidgetBox} from "../jackpot-widget/boxes/jackpot-widget.box";
import {JackpotWidgetBgBox} from "../jackpot-widget/boxes/bg/jackpot-widget-bg.box";
import {JackpotType} from "../../services/jackpots/jackpot.constants";
import {JackpotWidgetAmountSkeletonBox} from "../jackpot-widget/skeleton/boxes/jackpot-widget-amount-skeleton.box";
import {AmusnetWidgetLogoBox} from "./boxes/logo/amusnet-widget-logo.box";
import {getJackpotWidgetImagesByType} from "../jackpot-widget/jackpot-widget-images";
import {AmusnetWidgetContentContainerBox} from "./boxes/content-container/amusnet-widget-content-container.box";
import {AmusnetWidgetWrapperBox} from "./boxes/wrapper/amusnet-widget-wrapper.box";
import {AmusnetWidgetFooterBox} from "./boxes/footer/amusnet-widget-footer.box";
import {AmusnetWidgetLabelContainerBox} from "./boxes/label-container/amusnet-widget-label-container.box";
import {AmusnetWidgetLabelBox} from "./boxes/label/amusnet-widget-label.box";
import {AmusnetWidgetAmountBox} from "./boxes/amount/amusnet-widget-amount.box";
import {withServices} from "../../react-hooks/with-services";
import {ComponentServicesPick} from "../../services/component-services-pick";
import {IJackpotViewModel, NullableJackpotViewModel} from "../../services/jackpots/models/jackpot.view-model.interface";
import {observer} from "mobx-react";



interface AmusnetWidgetComponentProps extends ComponentServicesPick<'jackpots'> {
    WrapperBox?: React.ComponentType;
    widgetConfig: AmusnetJackpotWidgetConfiguration;
}


const ComponentBody: React.FC<AmusnetWidgetComponentProps> = observer((props) => {
    const [widgetWidth, setWidgetWidth] = useState<number>();
    const services = useServices();
    const WrapperBox = props.WrapperBox || React.Fragment;

    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    let aspectRatioBox: HTMLDivElement | null;
    let disposer: undefined | ((prevWidgetRef: HTMLDivElement) => void);


    useEffect(() => {
        const jackpot = props.jackpots.getJackpot(props.widgetConfig?.jackpotId);
        if (jackpot) {
            jackpot.startWidgetAmountAnimation();
        }

        return (() => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
        })
    }, [props.jackpots, props.widgetConfig?.jackpotId, services.theme.currentTheme]);

    const setWidgetWidthState = (entries: ResizeObserverEntry[]) => {
        // We know we are only watching the aspect ratio box element so we take the first element in the array.
        // We are not using this.aspectRatioBox directly because from a typescript point of view that could be null
        // and from a react component lifecycle perspective it could be null as well
        const targetElement = entries[0].target;
        const elementRectangle = targetElement.getBoundingClientRect();
        setWidgetWidth(elementRectangle.width);
    }

    const captureJackpotWidgetAspectRatioBoxRef = (widgetRef: HTMLDivElement | null) => {
        if (disposer) {
            disposer(aspectRatioBox);
            disposer = undefined;
        }
        aspectRatioBox = widgetRef;
        if (aspectRatioBox) {
            if (!resizeObserverRef.current) {
                resizeObserverRef.current = new ResizeObserver(setWidgetWidthState);
            }
            resizeObserverRef.current.observe(aspectRatioBox);
            disposer = (prevWidgetRef: HTMLDivElement) => {
                // At unmount, the resizeObserverInstance will be destroyed and the disposer will be called too late.
                // In that case, we don't do anything since the observer was disconnected at unmount
                if (resizeObserverRef.current) {
                    resizeObserverRef.current.unobserve(prevWidgetRef);
                }
            }
        }
    };

    const redirectToWidgetActionLink = async (actionLink: string) => {
        services.analytics.sendOrbitUserFlowEvent("Navigation", 'Jackpot_Widget', actionLink || 'Amusnet');

        services.window.scrollTo(0, 0);

        if (!actionLink) {
            let arenaPath = services.location.getBasePath;

            if (services.location.isHomePage) {
                arenaPath = (await services.sideMenu).getMenuItem('slotsMenuItem').getLinkUrl();
            }
            services.location.push(`${arenaPath}#filters=has-jackpot,vendor_egt`);
        } else {
            services.location.push(actionLink);
        }
    }

    const getJackpotWidgetLayout = (): JACKPOT_WIDGET_LAYOUT => {
        const isMobileTablet = services.mediaQuery.mAndAbove && services.mediaQuery.lAndBelow;

        return isMobileTablet ? JACKPOT_WIDGET_LAYOUT.PORTRAIT_TALL : JACKPOT_WIDGET_LAYOUT.PORTRAIT;
    }

    const getAmount = (jackpot: IJackpotViewModel) => {
        return services.currency.formatMoney(jackpot.widgetCurrentAnimatedAmountInCents);
    }

    const renderAmount = (amount: string) => {
        const amountParts = amount.split(',');

        return (
            <>
                <span>{amountParts[0]}</span>
                <span>,{amountParts[1]}</span>
            </>
        )
    }

    const renderContent = (jackpot: IJackpotViewModel): React.ReactNode => {
        return (<JackpotWidgetBgBox
            jackpotType={JackpotType.Amusnet}
            layout={getJackpotWidgetLayout()}
            isClickable={true}
        >
            <AmusnetWidgetLogoBox src={getJackpotWidgetImagesByType(services.theme.currentTheme, JackpotType.Amusnet).logo} alt="jackpot-logo" />
            <AmusnetWidgetContentContainerBox>
                <AmusnetWidgetLabelContainerBox>
                    <AmusnetWidgetLabelBox widgetWidth={widgetWidth}>Jackpot</AmusnetWidgetLabelBox>
                </AmusnetWidgetLabelContainerBox>
                <AmusnetWidgetAmountBox widgetWidth={widgetWidth}>
                    {renderAmount(getAmount(jackpot))}
                </AmusnetWidgetAmountBox>
            </AmusnetWidgetContentContainerBox>
            <AmusnetWidgetFooterBox />
        </JackpotWidgetBgBox>);
    }

    const renderSkeleton = () => {
        return (
            <JackpotWidgetAmountSkeletonBox />
        );
    }

    const shouldRenderSkeleton = (jackpot: NullableJackpotViewModel) => {
        return !jackpot;
    }

    const renderContentOrSkeleton = (jackpot: NullableJackpotViewModel) => {
        if (shouldRenderSkeleton(jackpot)) {
            return renderSkeleton();
        } else {
            return renderContent(jackpot);
        }
    }


    if (!props.widgetConfig || !props.widgetConfig.display) {
        return null;
    }
    const jackpot = props.jackpots.getJackpot(props.widgetConfig.jackpotId);

    return (
        <WrapperBox>
            <AmusnetWidgetWrapperBox ref={captureJackpotWidgetAspectRatioBoxRef} onClick={() => redirectToWidgetActionLink(props.widgetConfig.actionLink)}>
                <JackpotWidgetAspectRatioBox layout={getJackpotWidgetLayout()}>
                    <JackpotWidgetBox>
                        {renderContentOrSkeleton(jackpot)}
                    </JackpotWidgetBox>
                </JackpotWidgetAspectRatioBox>
            </AmusnetWidgetWrapperBox>
        </WrapperBox>
    )
})

export const AmusnetWidgetComponent = withServices(ComponentBody, ['jackpots']);